import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSearch, FiX, FiSliders } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const MealsFilter = ({ 
  onFilterChange, 
  categories = [],
  initialFilters = {}
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    categoryId: '',
    priceMin: '',
    priceMax: '',
    sortBy: 'default', // default, price_asc, price_desc
    ...initialFilters
  });

  const [priceRange, setPriceRange] = useState({ min: '', max: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    setPriceRange(prev => ({ ...prev, [name]: value }));
    setFilters(prev => ({ 
      ...prev, 
      priceMin: name === 'min' ? value : prev.priceMin,
      priceMax: name === 'max' ? value : prev.priceMax
    }));
  };

  const handleSearch = () => {
    onFilterChange(filters);
  };

  const handleReset = () => {
    const resetFilters = {
      search: '',
      categoryId: '',
      priceMin: '',
      priceMax: '',
      sortBy: 'default'
    };
    setFilters(resetFilters);
    setPriceRange({ min: '', max: '' });
    onFilterChange(resetFilters);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="bg-white dark:bg-n-8 rounded-2xl shadow-lg mb-8 overflow-hidden">
      {/* Search Bar - Always Visible */}
      <div className="p-4 border-b border-gray-200 dark:border-n-7">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-n-5" />
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder={`${t('search by name')} (${t('arabic or english')})...`}
              className="w-full pl-10 pr-4 body-2 py-3 border border-gray-200 dark:border-n-7 rounded-xl bg-gray-50 dark:bg-n-9 focus:outline-none focus:ring-2 focus:ring-color-1 focus:border-transparent transition-all"
          
            />
          </div>
          
          <button
            onClick={handleSearch}
            className="px-8 py-3 bg-color-1 text-white  rounded-xl hover:bg-color-1/90 transition-all duration-300 h6 shadow-md hover:shadow-lg"
          >
            {t('search')}
          </button>
          
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-6 py-3 border-2 border-gray-200 dark:border-n-7 font-kufi font-light rounded-xl hover:border-color-1 dark:hover:border-color-1 transition-all duration-300 flex items-center gap-2"
          >
            <FiSliders className={isExpanded ? 'text-color-1' : ''} />
            <span className={isExpanded ? 'text-color-1 ' : ''}>{t('advanced filters')}</span>
          </button>
        </div>
      </div>

      {/* Advanced Filters - Expandable */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-6 bg-gray-50 dark:bg-n-9">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-kufi font-light mb-2 text-n-6 dark:text-n-3">
                    {t('category')}
                  </label>
                  <select
                    name="categoryId"
                    value={filters.categoryId}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-200  body-2  dark:border-n-7 rounded-lg bg-white dark:bg-n-8 focus:outline-none focus:ring-2 focus:ring-color-1"
                  >
                    <option value="">{t('all categories')}</option>
                    {categories?.map(cat => (
                      <option key={cat._id} value={cat._id}>
                        {cat.titleAr || cat.titleEn}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="block text-sm font-kufi font-light mb-2 text-n-6 dark:text-n-3">
                    {t('price range')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      name="min"
                      value={priceRange.min}
                      onChange={handlePriceChange}
                      placeholder={t('min')}
                      className="w-full px-4 py-2 border border-gray-200 dark:border-n-7 font-kufi font-light rounded-lg bg-white dark:bg-n-8 focus:outline-none focus:ring-2 focus:ring-color-1"
                    />
                    <span className="text-gray-400 self-center">-</span>
                    <input
                      type="number"
                      name="max"
                      value={priceRange.max}
                      onChange={handlePriceChange}
                      placeholder={t('max')}
                      className="w-full px-4 py-2 border border-gray-200 dark:border-n-7 font-kufi font-light rounded-lg bg-white dark:bg-n-8 focus:outline-none focus:ring-2 focus:ring-color-1"
                    />
                  </div>
                </div>

                {/* Sort Options */}
                <div>
                  <label className="block text-sm font-kufi font-light mb-2 text-n-6 dark:text-n-3">
                    {t('sort by')}
                  </label>
                  <select
                    name="sortBy"
                    value={filters.sortBy}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-n-7 rounded-lg font-kufi font-light bg-white dark:bg-n-8 focus:outline-none focus:ring-2 focus:ring-color-1"
                  >
                    <option value="default">{t('auto / default')}</option>
                    <option value="price_asc">{t('price: low to high')}</option>
                    <option value="price_desc">{t('price: high to low')}</option>
                  </select>
                </div>

                {/* Reset Button */}
                <div className="flex items-end">
                  <button
                    onClick={handleReset}
                    className="w-full px-6 py-2 border font-kufi font-light border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <FiX />
                    {t('reset filters')}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active Filters Display */}
      {(filters.search || filters.categoryId || filters.priceMin || filters.priceMax) && (
        <div className="px-6 py-3 bg-gray-100 dark:bg-n-8 border-t border-gray-200 dark:border-n-7">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-n-5 dark:text-n-4">{t('active filters')}:</span>
            {filters.search && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-color-1/10 text-color-1 rounded-full text-sm">
                {t('search')}: {filters.search}
                <FiX 
                  size={14} 
                  className="cursor-pointer hover:text-color-1/70"
                  onClick={() => {
                    setFilters(prev => ({ ...prev, search: '' }));
                    onFilterChange({ ...filters, search: '' });
                  }}
                />
              </span>
            )}
            {filters.categoryId && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-color-1/10 text-color-1 rounded-full text-sm">
                {t('category')}: {categories.find(c => c._id === filters.categoryId)?.titleAr || filters.categoryId}
                <FiX 
                  size={14} 
                  className="cursor-pointer hover:text-color-1/70"
                  onClick={() => {
                    setFilters(prev => ({ ...prev, categoryId: '' }));
                    onFilterChange({ ...filters, categoryId: '' });
                  }}
                />
              </span>
            )}
            {(filters.priceMin || filters.priceMax) && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-color-1/10 text-color-1 rounded-full text-sm">
                {t('price')}: {filters.priceMin || '0'} - {filters.priceMax || '∞'}
                <FiX 
                  size={14} 
                  className="cursor-pointer hover:text-color-1/70"
                  onClick={() => {
                    setFilters(prev => ({ ...prev, priceMin: '', priceMax: '' }));
                    setPriceRange({ min: '', max: '' });
                    onFilterChange({ ...filters, priceMin: '', priceMax: '' });
                  }}
                />
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MealsFilter;